import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ClipboardList,
  Thermometer,
  Truck,
  SprayCan,
  ChefHat,
  FileText,
  Download,
  Plus,
  Trash2,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Upload,
  Calendar,
  Camera,
  Loader2,
  RefreshCw,
  BookOpen,
  ThermometerSun,
  ThermometerSnowflake,
  Package,
  Edit,
  Store,
  Eye,
  Settings,
  TrendingUp,
  BarChart3,
  PieChart,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour les requêtes API authentifiées
const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Erreur serveur" }));
    throw new Error(error.detail || "Erreur serveur");
  }
  return response.json();
};

// Upload de fichier
const uploadFile = async (file) => {
  const token = localStorage.getItem("token");
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(`${API_URL}/api/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) throw new Error("Erreur lors de l'upload");
  return response.json();
};

export default function HygienePage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("regles");
  const [loading, setLoading] = useState(true);
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [selectedFoodTruckId, setSelectedFoodTruckId] = useState(null);
  const [noFoodTruckAssociated, setNoFoodTruckAssociated] = useState(false);

  // Check if user is admin without food truck
  const isAdminWithoutFT = user?.role === "admin" && !user?.food_truck_id;

  useEffect(() => {
    const init = async () => {
      if (isAdminWithoutFT) {
        // Load food trucks list for admin to select
        try {
          const data = await fetchWithAuth(`${API_URL}/api/food-trucks`);
          setFoodTrucks(data);
          if (data.length > 0) {
            setSelectedFoodTruckId(data[0].id);
          }
        } catch (error) {
          toast.error("Erreur lors du chargement des food trucks");
        }
      }
      setLoading(false);
    };
    init();
  }, [isAdminWithoutFT]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Admin without food truck - show selector
  if (isAdminWithoutFT && !selectedFoodTruckId) {
    return (
      <div className="space-y-6" data-testid="hygiene-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-primary">
            Module Hygiène HACCP
          </h1>
          <p className="text-secondary mt-1">
            Mode supervision - Sélectionnez un food truck
          </p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Aucun food truck sélectionné</h3>
            <p className="text-secondary mb-4">
              En tant qu'administrateur, vous pouvez consulter les données hygiène de chaque food truck.
            </p>
            {foodTrucks.length > 0 ? (
              <Select onValueChange={setSelectedFoodTruckId}>
                <SelectTrigger className="w-64 mx-auto">
                  <SelectValue placeholder="Sélectionner un food truck" />
                </SelectTrigger>
                <SelectContent>
                  {foodTrucks.map((ft) => (
                    <SelectItem key={ft.id} value={ft.id}>
                      {ft.nom_entreprise || ft.nom_commercial || "Food Truck"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-amber-600">Aucun food truck enregistré</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0" data-testid="hygiene-page">
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-primary">
              Hygiène HACCP
            </h1>
            <p className="text-xs sm:text-sm text-secondary mt-1">
              Traçabilité, températures et conformité
            </p>
          </div>
          
          {/* Admin food truck selector */}
          {isAdminWithoutFT && selectedFoodTruckId && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 w-full sm:w-auto">
              <Eye className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
              <span className="text-xs sm:text-sm text-blue-800 hidden sm:inline">Mode supervision :</span>
              <Select value={selectedFoodTruckId} onValueChange={setSelectedFoodTruckId}>
                <SelectTrigger className="flex-1 sm:w-48 h-7 sm:h-8 text-xs sm:text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {foodTrucks.map((ft) => (
                    <SelectItem key={ft.id} value={ft.id}>
                      {ft.nom_entreprise || ft.nom_commercial || "Food Truck"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3 sm:space-y-4">
        <TabsList className="w-full overflow-x-auto flex flex-nowrap gap-0.5 sm:gap-1 p-1 bg-[#F0EBE6] scrollbar-hide">
          <TabsTrigger value="regles" className="flex-shrink-0 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5" data-testid="tab-regles">
            <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Règles</span>
          </TabsTrigger>
          <TabsTrigger value="receptions" className="flex-shrink-0 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5" data-testid="tab-receptions">
            <Package className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Réceptions</span>
          </TabsTrigger>
          <TabsTrigger value="tracabilite" className="flex-shrink-0 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5" data-testid="tab-tracabilite">
            <ClipboardList className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Traçabilité</span>
          </TabsTrigger>
          <TabsTrigger value="production" className="flex-shrink-0 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5" data-testid="tab-production">
            <ChefHat className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Production</span>
          </TabsTrigger>
          <TabsTrigger value="temperatures" className="flex-shrink-0 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5" data-testid="tab-temperatures">
            <Thermometer className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Frigos</span>
          </TabsTrigger>
          <TabsTrigger value="cuisson" className="flex-shrink-0 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5" data-testid="tab-cuisson">
            <ThermometerSun className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Cuisson</span>
          </TabsTrigger>
          <TabsTrigger value="nettoyage" className="flex-shrink-0 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5" data-testid="tab-nettoyage">
            <SprayCan className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Nettoyage</span>
          </TabsTrigger>
          <TabsTrigger value="statistiques" className="flex-shrink-0 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5" data-testid="tab-statistiques">
            <ClipboardList className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Statistiques</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="regles">
          <ReglesTab foodTruckId={selectedFoodTruckId} readOnly={isAdminWithoutFT} />
        </TabsContent>

        <TabsContent value="receptions">
          <ReceptionsTab foodTruckId={selectedFoodTruckId} readOnly={isAdminWithoutFT} />
        </TabsContent>

        <TabsContent value="tracabilite">
          <TracabiliteTab foodTruckId={selectedFoodTruckId} readOnly={isAdminWithoutFT} />
        </TabsContent>

        <TabsContent value="production">
          <ProductionTab foodTruckId={selectedFoodTruckId} readOnly={isAdminWithoutFT} />
        </TabsContent>

        <TabsContent value="temperatures">
          <TemperaturesTab foodTruckId={selectedFoodTruckId} readOnly={isAdminWithoutFT} />
        </TabsContent>

        <TabsContent value="cuisson">
          <CuissonTab foodTruckId={selectedFoodTruckId} readOnly={isAdminWithoutFT} />
        </TabsContent>

        <TabsContent value="nettoyage">
          <NettoyageTab foodTruckId={selectedFoodTruckId} readOnly={isAdminWithoutFT} />
        </TabsContent>

        <TabsContent value="statistiques">
          <StatistiquesTab foodTruckId={selectedFoodTruckId} />
        </TabsContent>
      </Tabs>

      {/* Section PMS et Export en bas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <PMSSection foodTruckId={selectedFoodTruckId} readOnly={isAdminWithoutFT} />
        <ExportSection foodTruckId={selectedFoodTruckId} />
      </div>
    </div>
  );
}

// ==================== RÈGLES IMPORTANTES ====================
function ReglesTab({ foodTruckId, readOnly }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newRule, setNewRule] = useState({ titre: "", contenu: "", categorie: "general", ordre: 0 });
  const [saving, setSaving] = useState(false);

  const loadRules = useCallback(async () => {
    try {
      const url = foodTruckId 
        ? `${API_URL}/api/hygiene/rules?food_truck_id=${foodTruckId}`
        : `${API_URL}/api/hygiene/rules`;
      const data = await fetchWithAuth(url);
      // Handle both array and object with is_admin_no_ft
      if (Array.isArray(data)) {
        setRules(data);
      } else if (data.rules) {
        setRules(data.rules);
      } else {
        setRules([]);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des règles");
    } finally {
      setLoading(false);
    }
  }, [foodTruckId]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleAddRule = async () => {
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/hygiene/rules`, {
        method: "POST",
        body: JSON.stringify(newRule),
      });
      toast.success("Règle ajoutée");
      setShowAddDialog(false);
      setNewRule({ titre: "", contenu: "", categorie: "general", ordre: 0 });
      loadRules();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm("Supprimer cette règle ?")) return;
    try {
      await fetchWithAuth(`${API_URL}/api/hygiene/rules/${ruleId}`, { method: "DELETE" });
      toast.success("Règle supprimée");
      loadRules();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const categories = {
    general: { label: "Général", color: "bg-blue-100 text-blue-800" },
    reception: { label: "Réception", color: "bg-green-100 text-green-800" },
    stockage: { label: "Stockage", color: "bg-yellow-100 text-yellow-800" },
    production: { label: "Production", color: "bg-orange-100 text-orange-800" },
    nettoyage: { label: "Nettoyage", color: "bg-purple-100 text-purple-800" },
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            Règles d'Hygiène Importantes
          </CardTitle>
          <CardDescription>Les bonnes pratiques HACCP à respecter</CardDescription>
        </div>
        {!readOnly && (
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-rule-btn">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {rules.map((rule) => (
            <div
              key={rule.id}
              className="p-4 bg-[#F9F7F5] rounded-lg border border-[#E5E0DC] hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-primary">{rule.titre}</h3>
                  <Badge className={categories[rule.categorie]?.color || "bg-gray-100"}>
                    {categories[rule.categorie]?.label || rule.categorie}
                  </Badge>
                </div>
                {!rule.is_default && !readOnly && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRule(rule.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <p className="text-secondary text-sm whitespace-pre-wrap">{rule.contenu}</p>
            </div>
          ))}
        </div>

        {/* Dialog ajout règle */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajouter une règle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Titre</Label>
                <Input
                  value={newRule.titre}
                  onChange={(e) => setNewRule({ ...newRule, titre: e.target.value })}
                  placeholder="Ex: Port des gants obligatoire"
                />
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select value={newRule.categorie} onValueChange={(v) => setNewRule({ ...newRule, categorie: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Général</SelectItem>
                    <SelectItem value="reception">Réception</SelectItem>
                    <SelectItem value="stockage">Stockage</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                    <SelectItem value="nettoyage">Nettoyage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Contenu</Label>
                <Textarea
                  value={newRule.contenu}
                  onChange={(e) => setNewRule({ ...newRule, contenu: e.target.value })}
                  placeholder="Détails de la règle..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
              <Button onClick={handleAddRule} disabled={saving || !newRule.titre || !newRule.contenu}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Ajouter
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ==================== RECEPTIONS ====================
function ReceptionsTab({ foodTruckId, readOnly, onReceptionCreated }) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recentReceptions, setRecentReceptions] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [newReception, setNewReception] = useState({
    produit: "",
    fournisseur: "",
    date_reception: new Date().toISOString().split("T")[0],
    dlc: "",
    numero_lot: "",
    temperature_reception: "",
    quantite: "",
    unite: "kg",
    photo_bon_livraison: null,
    conforme: true,
    commentaire: "",
  });

  // Load recent receptions
  const loadRecentReceptions = useCallback(async () => {
    try {
      const url = foodTruckId 
        ? `${API_URL}/api/hygiene/receptions?days=7&food_truck_id=${foodTruckId}`
        : `${API_URL}/api/hygiene/receptions?days=7`;
      const data = await fetchWithAuth(url);
      setRecentReceptions(Array.isArray(data) ? data : []);
    } catch (error) {
      // Silent fail
    } finally {
      setLoadingRecent(false);
    }
  }, [foodTruckId]);

  useEffect(() => {
    loadRecentReceptions();
  }, [loadRecentReceptions]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadFile(file);
      setNewReception({ ...newReception, photo_bon_livraison: result.url });
      toast.success("Photo uploadée");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newReception.produit || !newReception.fournisseur || !newReception.dlc || !newReception.numero_lot) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/hygiene/receptions`, {
        method: "POST",
        body: JSON.stringify({
          ...newReception,
          temperature_reception: parseFloat(newReception.temperature_reception) || 0,
        }),
      });
      toast.success("Réception enregistrée");
      setShowAddDialog(false);
      setNewReception({
        produit: "",
        fournisseur: "",
        date_reception: new Date().toISOString().split("T")[0],
        dlc: "",
        numero_lot: "",
        temperature_reception: "",
        quantite: "",
        unite: "kg",
        photo_bon_livraison: null,
        conforme: true,
        commentaire: "",
      });
      // Reload the list
      loadRecentReceptions();
      // Notify parent if callback provided
      if (onReceptionCreated) onReceptionCreated();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            Procédure de Réception
          </CardTitle>
          <CardDescription>Enregistrez les réceptions de marchandises</CardDescription>
        </div>
        {!readOnly && (
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-reception-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle réception
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-amber-800 mb-2">Contrôles à effectuer :</h4>
          <ul className="text-sm text-amber-700 space-y-1">
            <li>• Vérifier la température des produits frais (&lt; 4°C)</li>
            <li>• Contrôler l'intégrité des emballages</li>
            <li>• Vérifier les DLC/DDM</li>
            <li>• Photographier le bon de livraison</li>
          </ul>
        </div>

        {!readOnly && (
          <Button
            onClick={() => setShowAddDialog(true)}
            className="w-full h-24 border-2 border-dashed border-primary/30 bg-primary/5 hover:bg-primary/10"
            variant="ghost"
          >
            <div className="flex flex-col items-center">
              <Truck className="w-8 h-8 text-primary mb-2" />
              <span>Enregistrer une nouvelle réception</span>
            </div>
          </Button>
        )}

        {/* Réceptions récentes */}
        {recentReceptions.length > 0 && (
          <div className="mt-6">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Réceptions récentes (7 derniers jours)
            </h4>
            <div className="space-y-2">
              {recentReceptions.slice(0, 5).map((r) => (
                <div key={r.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div>
                    <span className="font-medium">{r.produit}</span>
                    <span className="text-sm text-secondary ml-2">({r.fournisseur})</span>
                  </div>
                  <div className="text-sm text-secondary">
                    {r.date_reception?.split("T")[0]} | Lot: {r.numero_lot}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nouvelle Réception</DialogTitle>
              <DialogDescription>Enregistrez les détails de la livraison</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Produit *</Label>
                  <Input
                    value={newReception.produit}
                    onChange={(e) => setNewReception({ ...newReception, produit: e.target.value })}
                    placeholder="Ex: Poulet frais"
                  />
                </div>
                <div>
                  <Label>Fournisseur *</Label>
                  <Input
                    value={newReception.fournisseur}
                    onChange={(e) => setNewReception({ ...newReception, fournisseur: e.target.value })}
                    placeholder="Ex: Metro"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date réception</Label>
                  <Input
                    type="date"
                    value={newReception.date_reception}
                    onChange={(e) => setNewReception({ ...newReception, date_reception: e.target.value })}
                  />
                </div>
                <div>
                  <Label>DLC *</Label>
                  <Input
                    type="date"
                    value={newReception.dlc}
                    onChange={(e) => setNewReception({ ...newReception, dlc: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>N° de lot *</Label>
                  <Input
                    value={newReception.numero_lot}
                    onChange={(e) => setNewReception({ ...newReception, numero_lot: e.target.value })}
                    placeholder="Ex: LOT2024123"
                  />
                </div>
                <div>
                  <Label>Température à réception (°C)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newReception.temperature_reception}
                    onChange={(e) => setNewReception({ ...newReception, temperature_reception: e.target.value })}
                    placeholder="Ex: 3.5"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantité</Label>
                  <Input
                    value={newReception.quantite}
                    onChange={(e) => setNewReception({ ...newReception, quantite: e.target.value })}
                    placeholder="Ex: 10"
                  />
                </div>
                <div>
                  <Label>Unité</Label>
                  <Select value={newReception.unite} onValueChange={(v) => setNewReception({ ...newReception, unite: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="g">g</SelectItem>
                      <SelectItem value="L">L</SelectItem>
                      <SelectItem value="unités">unités</SelectItem>
                      <SelectItem value="cartons">cartons</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Photo bon de livraison</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="flex-1"
                  />
                  {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {newReception.photo_bon_livraison && (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="conforme"
                  checked={newReception.conforme}
                  onCheckedChange={(checked) => setNewReception({ ...newReception, conforme: checked })}
                />
                <Label htmlFor="conforme">Livraison conforme</Label>
              </div>
              <div>
                <Label>Commentaire</Label>
                <Textarea
                  value={newReception.commentaire}
                  onChange={(e) => setNewReception({ ...newReception, commentaire: e.target.value })}
                  placeholder="Observations éventuelles..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ==================== TRACABILITE ====================
function TracabiliteTab({ foodTruckId, readOnly }) {
  const [receptions, setReceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, warning, expired

  const loadReceptions = useCallback(async () => {
    try {
      const url = foodTruckId 
        ? `${API_URL}/api/hygiene/receptions?days=90&food_truck_id=${foodTruckId}`
        : `${API_URL}/api/hygiene/receptions?days=90`;
      const data = await fetchWithAuth(url);
      setReceptions(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [foodTruckId]);

  useEffect(() => {
    loadReceptions();
  }, [loadReceptions]);

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette entrée ?")) return;
    try {
      await fetchWithAuth(`${API_URL}/api/hygiene/receptions/${id}`, { method: "DELETE" });
      toast.success("Entrée supprimée");
      loadReceptions();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const filteredReceptions = receptions.filter((r) => {
    if (filter === "all") return true;
    if (filter === "warning") return r.dlc_status === "warning";
    if (filter === "expired") return r.dlc_status === "expired";
    return true;
  });

  const getStatusBadge = (status, days) => {
    if (status === "expired") {
      return <Badge className="bg-red-500">Périmé</Badge>;
    }
    if (status === "warning") {
      return <Badge className="bg-amber-500">J-{days}</Badge>;
    }
    return <Badge className="bg-green-500">OK</Badge>;
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" />
              Traçabilité des Produits
            </CardTitle>
            <CardDescription>Historique des réceptions et suivi des DLC</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant={filter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("all")}
            >
              Tous ({receptions.length})
            </Button>
            <Button
              variant={filter === "warning" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("warning")}
              className={filter !== "warning" ? "text-amber-600 border-amber-300" : ""}
            >
              <AlertTriangle className="w-4 h-4 mr-1" />
              DLC proche
            </Button>
            <Button
              variant={filter === "expired" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("expired")}
              className={filter !== "expired" ? "text-red-600 border-red-300" : ""}
            >
              <AlertCircle className="w-4 h-4 mr-1" />
              Périmés
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredReceptions.length === 0 ? (
          <p className="text-center text-secondary py-8">Aucune réception enregistrée</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Produit</TableHead>
                  <TableHead>Fournisseur</TableHead>
                  <TableHead>N° Lot</TableHead>
                  <TableHead>DLC</TableHead>
                  <TableHead>T°</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReceptions.map((r) => (
                  <TableRow key={r.id} className={r.dlc_status === "expired" ? "bg-red-50" : r.dlc_status === "warning" ? "bg-amber-50" : ""}>
                    <TableCell>{r.date_reception?.split("T")[0]}</TableCell>
                    <TableCell className="font-medium">{r.produit}</TableCell>
                    <TableCell>{r.fournisseur}</TableCell>
                    <TableCell><code className="text-xs bg-gray-100 px-1 rounded">{r.numero_lot}</code></TableCell>
                    <TableCell>{r.dlc?.split("T")[0]}</TableCell>
                    <TableCell>{r.temperature_reception}°C</TableCell>
                    <TableCell>{getStatusBadge(r.dlc_status, r.days_until_dlc)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== PRODUCTION ====================
function ProductionTab({ foodTruckId, readOnly }) {
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newProd, setNewProd] = useState({
    date: new Date().toISOString().split("T")[0],
    plat: "",
    quantite: "",
    unite: "portions",
    temperature_cuisson: "",
    temperature_coeur: "",
    heure_preparation: "",
    heure_fin: "",
    dlc_produit_fini: "",
    conforme: true,
    commentaire: "",
  });

  const loadProductions = async () => {
    try {
      const data = await fetchWithAuth(`${API_URL}/api/hygiene/production?days=30`);
      setProductions(data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProductions();
  }, []);

  const handleSubmit = async () => {
    if (!newProd.plat || !newProd.quantite) {
      toast.error("Veuillez remplir le plat et la quantité");
      return;
    }
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/hygiene/production`, {
        method: "POST",
        body: JSON.stringify({
          ...newProd,
          quantite: parseInt(newProd.quantite) || 0,
          temperature_cuisson: newProd.temperature_cuisson ? parseFloat(newProd.temperature_cuisson) : null,
          temperature_coeur: newProd.temperature_coeur ? parseFloat(newProd.temperature_coeur) : null,
        }),
      });
      toast.success("Fiche de production enregistrée");
      setShowAddDialog(false);
      setNewProd({
        date: new Date().toISOString().split("T")[0],
        plat: "",
        quantite: "",
        unite: "portions",
        temperature_cuisson: "",
        temperature_coeur: "",
        heure_preparation: "",
        heure_fin: "",
        dlc_produit_fini: "",
        conforme: true,
        commentaire: "",
      });
      loadProductions();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <ChefHat className="w-5 h-5 text-primary" />
            Fiches de Production
          </CardTitle>
          <CardDescription>Suivi des plats préparés quotidiennement</CardDescription>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-production-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle fiche
        </Button>
      </CardHeader>
      <CardContent>
        {productions.length === 0 ? (
          <div className="text-center py-8">
            <ChefHat className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-secondary">Aucune fiche de production</p>
            <Button className="mt-4" onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Créer ma première fiche
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Plat</TableHead>
                  <TableHead>Quantité</TableHead>
                  <TableHead>T° Cuisson</TableHead>
                  <TableHead>T° Coeur</TableHead>
                  <TableHead>Horaires</TableHead>
                  <TableHead>Conforme</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {productions.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.date?.split("T")[0]}</TableCell>
                    <TableCell className="font-medium">{p.plat}</TableCell>
                    <TableCell>{p.quantite} {p.unite}</TableCell>
                    <TableCell>{p.temperature_cuisson ? `${p.temperature_cuisson}°C` : "-"}</TableCell>
                    <TableCell>{p.temperature_coeur ? `${p.temperature_coeur}°C` : "-"}</TableCell>
                    <TableCell>{p.heure_preparation && p.heure_fin ? `${p.heure_preparation} - ${p.heure_fin}` : "-"}</TableCell>
                    <TableCell>
                      {p.conforme ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouvelle Fiche de Production</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={newProd.date}
                    onChange={(e) => setNewProd({ ...newProd, date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Plat *</Label>
                  <Input
                    value={newProd.plat}
                    onChange={(e) => setNewProd({ ...newProd, plat: e.target.value })}
                    placeholder="Ex: Poulet rôti"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantité *</Label>
                  <Input
                    type="number"
                    value={newProd.quantite}
                    onChange={(e) => setNewProd({ ...newProd, quantite: e.target.value })}
                    placeholder="Ex: 50"
                  />
                </div>
                <div>
                  <Label>Unité</Label>
                  <Select value={newProd.unite} onValueChange={(v) => setNewProd({ ...newProd, unite: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portions">portions</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="L">L</SelectItem>
                      <SelectItem value="unités">unités</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>T° Cuisson (°C)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProd.temperature_cuisson}
                    onChange={(e) => setNewProd({ ...newProd, temperature_cuisson: e.target.value })}
                    placeholder="Ex: 180"
                  />
                </div>
                <div>
                  <Label>T° à coeur (°C)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProd.temperature_coeur}
                    onChange={(e) => setNewProd({ ...newProd, temperature_coeur: e.target.value })}
                    placeholder="Ex: 74"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Heure début</Label>
                  <Input
                    type="time"
                    value={newProd.heure_preparation}
                    onChange={(e) => setNewProd({ ...newProd, heure_preparation: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Heure fin</Label>
                  <Input
                    type="time"
                    value={newProd.heure_fin}
                    onChange={(e) => setNewProd({ ...newProd, heure_fin: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="conforme-prod"
                  checked={newProd.conforme}
                  onCheckedChange={(checked) => setNewProd({ ...newProd, conforme: checked })}
                />
                <Label htmlFor="conforme-prod">Production conforme</Label>
              </div>
              <div>
                <Label>Commentaire</Label>
                <Textarea
                  value={newProd.commentaire}
                  onChange={(e) => setNewProd({ ...newProd, commentaire: e.target.value })}
                  placeholder="Observations..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ==================== TEMPERATURES FRIGOS ====================
function TemperaturesTab({ foodTruckId, readOnly }) {
  const [temperatures, setTemperatures] = useState([]);
  const [equipements, setEquipements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEquipementDialog, setShowEquipementDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newEquipement, setNewEquipement] = useState("");
  const [newTemp, setNewTemp] = useState({
    equipement: "Frigo 1",
    temperature: "",
    moment: "matin",
    date: new Date().toISOString().split("T")[0],
    action_corrective: "",
  });

  const loadData = async () => {
    try {
      const [tempsData, equipData] = await Promise.all([
        fetchWithAuth(`${API_URL}/api/hygiene/temperatures?days=7`),
        fetchWithAuth(`${API_URL}/api/hygiene/temperatures/equipements`),
      ]);
      setTemperatures(tempsData);
      setEquipements(equipData);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEquipement = async () => {
    if (!newEquipement.trim()) {
      toast.error("Veuillez entrer un nom d'équipement");
      return;
    }
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/hygiene/temperatures/equipements`, {
        method: "POST",
        body: JSON.stringify({ nom: newEquipement.trim() }),
      });
      toast.success("Équipement ajouté");
      setNewEquipement("");
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEquipement = async (nom) => {
    if (!window.confirm(`Supprimer l'équipement "${nom}" ?`)) return;
    try {
      await fetchWithAuth(`${API_URL}/api/hygiene/temperatures/equipements/${encodeURIComponent(nom)}`, {
        method: "DELETE",
      });
      toast.success("Équipement supprimé");
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async () => {
    if (!newTemp.temperature) {
      toast.error("Veuillez saisir la température");
      return;
    }
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/hygiene/temperatures`, {
        method: "POST",
        body: JSON.stringify({
          ...newTemp,
          temperature: parseFloat(newTemp.temperature),
        }),
      });
      toast.success("Relevé enregistré");
      setShowAddDialog(false);
      setNewTemp({
        equipement: "Frigo 1",
        temperature: "",
        moment: "matin",
        date: new Date().toISOString().split("T")[0],
        action_corrective: "",
      });
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  // Grouper par date et équipement
  const groupedByDate = temperatures.reduce((acc, t) => {
    const date = t.date?.split("T")[0];
    if (!acc[date]) acc[date] = {};
    if (!acc[date][t.equipement]) acc[date][t.equipement] = {};
    acc[date][t.equipement][t.moment] = t;
    return acc;
  }, {});

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Thermometer className="w-5 h-5 text-primary" />
            Relevés de Température
          </CardTitle>
          <CardDescription>Suivi des frigos et congélateurs (2x/jour)</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowEquipementDialog(true)} data-testid="manage-equipment-btn">
            <Settings className="w-4 h-4 mr-2" />
            Gérer équipements
          </Button>
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-temperature-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau relevé
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {equipements.map((eq) => {
            const todayData = groupedByDate[new Date().toISOString().split("T")[0]]?.[eq];
            const isCongel = eq.toLowerCase().includes("congel");
            return (
              <Card key={eq} className="bg-gradient-to-br from-blue-50 to-cyan-50 relative group">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{eq}</span>
                    <div className="flex items-center gap-2">
                      {isCongel ? (
                        <ThermometerSnowflake className="w-5 h-5 text-blue-500" />
                      ) : (
                        <Thermometer className="w-5 h-5 text-cyan-500" />
                      )}
                      <button 
                        onClick={() => handleDeleteEquipement(eq)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-white/50 rounded p-2">
                      <span className="text-gray-500">Matin</span>
                      <p className={`font-bold ${todayData?.matin?.conforme === false ? "text-red-500" : ""}`}>
                        {todayData?.matin?.temperature ?? "-"}°C
                      </p>
                    </div>
                    <div className="bg-white/50 rounded p-2">
                      <span className="text-gray-500">Soir</span>
                      <p className={`font-bold ${todayData?.soir?.conforme === false ? "text-red-500" : ""}`}>
                        {todayData?.soir?.temperature ?? "-"}°C
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Historique */}
        <h4 className="font-semibold mb-2">Historique (7 derniers jours)</h4>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Équipement</TableHead>
                <TableHead>Moment</TableHead>
                <TableHead>Température</TableHead>
                <TableHead>Conforme</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {temperatures.slice(0, 20).map((t) => (
                <TableRow key={t.id} className={!t.conforme ? "bg-red-50" : ""}>
                  <TableCell>{t.date?.split("T")[0]}</TableCell>
                  <TableCell>{t.equipement}</TableCell>
                  <TableCell className="capitalize">{t.moment}</TableCell>
                  <TableCell className={!t.conforme ? "text-red-600 font-bold" : ""}>{t.temperature}°C</TableCell>
                  <TableCell>
                    {t.conforme ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouveau Relevé de Température</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Équipement</Label>
                <Select value={newTemp.equipement} onValueChange={(v) => setNewTemp({ ...newTemp, equipement: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {equipements.map((eq) => (
                      <SelectItem key={eq} value={eq}>{eq}</SelectItem>
                    ))}
                    <SelectItem value="Frigo 3">Frigo 3 (nouveau)</SelectItem>
                    <SelectItem value="Congélateur 2">Congélateur 2 (nouveau)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Température (°C) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newTemp.temperature}
                    onChange={(e) => setNewTemp({ ...newTemp, temperature: e.target.value })}
                    placeholder="Ex: 3.5"
                  />
                </div>
                <div>
                  <Label>Moment</Label>
                  <Select value={newTemp.moment} onValueChange={(v) => setNewTemp({ ...newTemp, moment: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="matin">Matin</SelectItem>
                      <SelectItem value="soir">Soir</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newTemp.date}
                  onChange={(e) => setNewTemp({ ...newTemp, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Action corrective (si hors norme)</Label>
                <Textarea
                  value={newTemp.action_corrective}
                  onChange={(e) => setNewTemp({ ...newTemp, action_corrective: e.target.value })}
                  placeholder="Ex: Réglage du thermostat, vérification joint..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Dialog pour gérer les équipements */}
        <Dialog open={showEquipementDialog} onOpenChange={setShowEquipementDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Gérer mes équipements
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newEquipement}
                  onChange={(e) => setNewEquipement(e.target.value)}
                  placeholder="Nom du nouvel équipement (ex: Frigo 3)"
                />
                <Button onClick={handleAddEquipement} disabled={saving}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                {equipements.length === 0 ? (
                  <p className="p-4 text-center text-muted-foreground">Aucun équipement</p>
                ) : (
                  equipements.map((eq) => (
                    <div key={eq} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div className="flex items-center gap-2">
                        {eq.toLowerCase().includes("congel") ? (
                          <ThermometerSnowflake className="w-4 h-4 text-blue-500" />
                        ) : (
                          <Thermometer className="w-4 h-4 text-cyan-500" />
                        )}
                        <span>{eq}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteEquipement(eq)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEquipementDialog(false)}>Fermer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ==================== CUISSON / REFROIDISSEMENT ====================
function CuissonTab({ foodTruckId, readOnly }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newLog, setNewLog] = useState({
    type_operation: "cuisson",
    produit: "",
    temperature_debut: "",
    temperature_fin: "",
    heure_debut: "",
    heure_fin: "",
    date: new Date().toISOString().split("T")[0],
    commentaire: "",
  });

  const loadLogs = async () => {
    try {
      const data = await fetchWithAuth(`${API_URL}/api/hygiene/temperatures/cooking?days=30`);
      setLogs(data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const handleSubmit = async () => {
    if (!newLog.produit || !newLog.temperature_debut || !newLog.temperature_fin) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/hygiene/temperatures/cooking`, {
        method: "POST",
        body: JSON.stringify({
          ...newLog,
          temperature_debut: parseFloat(newLog.temperature_debut),
          temperature_fin: parseFloat(newLog.temperature_fin),
        }),
      });
      toast.success("Enregistrement effectué");
      setShowAddDialog(false);
      setNewLog({
        type_operation: "cuisson",
        produit: "",
        temperature_debut: "",
        temperature_fin: "",
        heure_debut: "",
        heure_fin: "",
        date: new Date().toISOString().split("T")[0],
        commentaire: "",
      });
      loadLogs();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <ThermometerSun className="w-5 h-5 text-primary" />
            Montée / Descente en Température
          </CardTitle>
          <CardDescription>Suivi des cuissons et refroidissements</CardDescription>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-cooking-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau suivi
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-orange-800 mb-2">Cuisson</h4>
              <p className="text-sm text-orange-700">
                Température à coeur minimale : <strong>+63°C</strong><br />
                Volailles : <strong>+74°C</strong>
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-blue-800 mb-2">Refroidissement</h4>
              <p className="text-sm text-blue-700">
                Passer de +63°C à <strong>+10°C</strong><br />
                en moins de <strong>2 heures</strong>
              </p>
            </CardContent>
          </Card>
        </div>

        {logs.length === 0 ? (
          <p className="text-center text-secondary py-8">Aucun suivi enregistré</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Produit</TableHead>
                  <TableHead>T° Début</TableHead>
                  <TableHead>T° Fin</TableHead>
                  <TableHead>Durée</TableHead>
                  <TableHead>Conforme</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((l) => (
                  <TableRow key={l.id} className={!l.conforme ? "bg-red-50" : ""}>
                    <TableCell>{l.date?.split("T")[0]}</TableCell>
                    <TableCell>
                      <Badge className={l.type_operation === "cuisson" ? "bg-orange-500" : "bg-blue-500"}>
                        {l.type_operation === "cuisson" ? "Cuisson" : "Refroid."}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{l.produit}</TableCell>
                    <TableCell>{l.temperature_debut}°C</TableCell>
                    <TableCell className={!l.conforme ? "text-red-600 font-bold" : ""}>{l.temperature_fin}°C</TableCell>
                    <TableCell>{l.heure_debut && l.heure_fin ? `${l.heure_debut} - ${l.heure_fin}` : "-"}</TableCell>
                    <TableCell>
                      {l.conforme ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Suivi Cuisson / Refroidissement</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Type d'opération</Label>
                <Select value={newLog.type_operation} onValueChange={(v) => setNewLog({ ...newLog, type_operation: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cuisson">Cuisson (montée en T°)</SelectItem>
                    <SelectItem value="refroidissement">Refroidissement (descente en T°)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Produit *</Label>
                <Input
                  value={newLog.produit}
                  onChange={(e) => setNewLog({ ...newLog, produit: e.target.value })}
                  placeholder="Ex: Sauce bolognaise"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>T° Début (°C) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newLog.temperature_debut}
                    onChange={(e) => setNewLog({ ...newLog, temperature_debut: e.target.value })}
                    placeholder={newLog.type_operation === "cuisson" ? "Ex: 20" : "Ex: 70"}
                  />
                </div>
                <div>
                  <Label>T° Fin (°C) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newLog.temperature_fin}
                    onChange={(e) => setNewLog({ ...newLog, temperature_fin: e.target.value })}
                    placeholder={newLog.type_operation === "cuisson" ? "Ex: 74" : "Ex: 8"}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Heure début</Label>
                  <Input
                    type="time"
                    value={newLog.heure_debut}
                    onChange={(e) => setNewLog({ ...newLog, heure_debut: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Heure fin</Label>
                  <Input
                    type="time"
                    value={newLog.heure_fin}
                    onChange={(e) => setNewLog({ ...newLog, heure_fin: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newLog.date}
                  onChange={(e) => setNewLog({ ...newLog, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Commentaire</Label>
                <Textarea
                  value={newLog.commentaire}
                  onChange={(e) => setNewLog({ ...newLog, commentaire: e.target.value })}
                  placeholder="Observations..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ==================== NETTOYAGE ====================
function NettoyageTab({ foodTruckId, readOnly }) {
  const [tasks, setTasks] = useState([]);
  const [todayStatus, setTodayStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTask, setNewTask] = useState({
    nom: "",
    zone: "Cuisine",
    frequence: "quotidien",
    jour_semaine: null,
    produit_nettoyage: "",
    instructions: "",
  });

  const loadData = async () => {
    try {
      const [tasksData, todayData] = await Promise.all([
        fetchWithAuth(`${API_URL}/api/hygiene/cleaning/tasks`),
        fetchWithAuth(`${API_URL}/api/hygiene/cleaning/today`),
      ]);
      setTasks(tasksData);
      setTodayStatus(todayData);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleToggleTask = async (task) => {
    if (task.completed) return; // Déjà fait
    try {
      await fetchWithAuth(`${API_URL}/api/hygiene/cleaning/logs`, {
        method: "POST",
        body: JSON.stringify({
          task_id: task.id,
          date: new Date().toISOString(),
        }),
      });
      toast.success(`"${task.nom}" marqué comme fait`);
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.nom) {
      toast.error("Veuillez saisir un nom de tâche");
      return;
    }
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/hygiene/cleaning/tasks`, {
        method: "POST",
        body: JSON.stringify(newTask),
      });
      toast.success("Tâche ajoutée");
      setShowAddDialog(false);
      setNewTask({
        nom: "",
        zone: "Cuisine",
        frequence: "quotidien",
        jour_semaine: null,
        produit_nettoyage: "",
        instructions: "",
      });
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Supprimer cette tâche ?")) return;
    try {
      await fetchWithAuth(`${API_URL}/api/hygiene/cleaning/tasks/${taskId}`, { method: "DELETE" });
      toast.success("Tâche supprimée");
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const completedCount = todayStatus.filter((t) => t.completed).length;
  const totalCount = todayStatus.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const joursSemaine = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Checklist du jour */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SprayCan className="w-5 h-5 text-primary" />
            Checklist du Jour
          </CardTitle>
          <CardDescription>
            {completedCount}/{totalCount} tâches effectuées
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Barre de progression */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div
              className={`h-3 rounded-full transition-all ${progress === 100 ? "bg-green-500" : "bg-primary"}`}
              style={{ width: `${progress}%` }}
            />
          </div>

          {todayStatus.length === 0 ? (
            <p className="text-secondary text-center py-4">Aucune tâche prévue aujourd'hui</p>
          ) : (
            <div className="space-y-2">
              {todayStatus.map((task) => (
                <div
                  key={task.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                    task.completed
                      ? "bg-green-50 border-green-200"
                      : "bg-white hover:bg-gray-50 border-gray-200"
                  }`}
                  onClick={() => handleToggleTask(task)}
                >
                  <Checkbox checked={task.completed} disabled={task.completed} />
                  <div className="flex-1">
                    <span className={task.completed ? "line-through text-gray-400" : "font-medium"}>
                      {task.nom}
                    </span>
                    <span className="text-xs text-secondary ml-2">({task.zone})</span>
                  </div>
                  {task.completed && <CheckCircle className="w-5 h-5 text-green-500" />}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan de nettoyage complet */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Plan de Nettoyage</CardTitle>
            <CardDescription>Paramétrez vos tâches de nettoyage</CardDescription>
          </div>
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-cleaning-task-btn">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter tâche
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tâche</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead>Fréquence</TableHead>
                  <TableHead>Produit</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell className="font-medium">{task.nom}</TableCell>
                    <TableCell>{task.zone}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {task.frequence}
                        {task.frequence === "hebdomadaire" && task.jour_semaine !== null && (
                          <span className="ml-1">({joursSemaine[task.jour_semaine]})</span>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-secondary">{task.produit_nettoyage || "-"}</TableCell>
                    <TableCell>
                      {!task.is_default && (
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteTask(task.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Dialog */}
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Ajouter une Tâche de Nettoyage</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Nom de la tâche *</Label>
                  <Input
                    value={newTask.nom}
                    onChange={(e) => setNewTask({ ...newTask, nom: e.target.value })}
                    placeholder="Ex: Nettoyage hotte"
                  />
                </div>
                <div>
                  <Label>Zone</Label>
                  <Select value={newTask.zone} onValueChange={(v) => setNewTask({ ...newTask, zone: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cuisine">Cuisine</SelectItem>
                      <SelectItem value="Sol">Sol</SelectItem>
                      <SelectItem value="Stockage">Stockage</SelectItem>
                      <SelectItem value="Ventilation">Ventilation</SelectItem>
                      <SelectItem value="Déchets">Déchets</SelectItem>
                      <SelectItem value="Extérieur">Extérieur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fréquence</Label>
                  <Select value={newTask.frequence} onValueChange={(v) => setNewTask({ ...newTask, frequence: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quotidien">Quotidien</SelectItem>
                      <SelectItem value="hebdomadaire">Hebdomadaire</SelectItem>
                      <SelectItem value="mensuel">Mensuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newTask.frequence === "hebdomadaire" && (
                  <div>
                    <Label>Jour de la semaine</Label>
                    <Select
                      value={newTask.jour_semaine?.toString() || "0"}
                      onValueChange={(v) => setNewTask({ ...newTask, jour_semaine: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {joursSemaine.map((jour, i) => (
                          <SelectItem key={i} value={i.toString()}>{jour}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Produit de nettoyage</Label>
                  <Input
                    value={newTask.produit_nettoyage}
                    onChange={(e) => setNewTask({ ...newTask, produit_nettoyage: e.target.value })}
                    placeholder="Ex: Dégraissant alimentaire"
                  />
                </div>
                <div>
                  <Label>Instructions</Label>
                  <Textarea
                    value={newTask.instructions}
                    onChange={(e) => setNewTask({ ...newTask, instructions: e.target.value })}
                    placeholder="Instructions détaillées..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
                <Button onClick={handleAddTask} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Ajouter
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== PMS SECTION ====================
function PMSSection({ foodTruckId, readOnly }) {
  const [pms, setPms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [newPms, setNewPms] = useState({ nom: "Plan de Maîtrise Sanitaire", version: "1.0", fichier_url: "" });

  const loadPms = async () => {
    try {
      const data = await fetchWithAuth(`${API_URL}/api/hygiene/pms`);
      setPms(data);
    } catch (error) {
      // Pas de PMS, c'est normal
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPms();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadFile(file);
      setNewPms({ ...newPms, fichier_url: result.url });
      toast.success("Fichier uploadé");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleSavePms = async () => {
    if (!newPms.fichier_url) {
      toast.error("Veuillez uploader un fichier");
      return;
    }
    try {
      await fetchWithAuth(`${API_URL}/api/hygiene/pms`, {
        method: "POST",
        body: JSON.stringify(newPms),
      });
      toast.success("PMS enregistré");
      setShowUploadDialog(false);
      loadPms();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          Plan de Maîtrise Sanitaire (PMS)
        </CardTitle>
        <CardDescription>Votre document HACCP complet</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        ) : pms ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-green-600" />
                <div>
                  <p className="font-medium">{pms.nom}</p>
                  <p className="text-sm text-secondary">
                    Version {pms.version} - Mis à jour le {pms.date_mise_a_jour?.split("T")[0]}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" asChild>
                  <a href={pms.fichier_url} target="_blank" rel="noopener noreferrer">
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger
                  </a>
                </Button>
                <Button size="sm" onClick={() => setShowUploadDialog(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Mettre à jour
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-secondary mb-4">Aucun PMS uploadé</p>
            <Button onClick={() => setShowUploadDialog(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Uploader mon PMS
            </Button>
          </div>
        )}

        {/* Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Uploader le PMS</DialogTitle>
              <DialogDescription>
                Uploadez votre Plan de Maîtrise Sanitaire (PDF recommandé)
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nom du document</Label>
                <Input
                  value={newPms.nom}
                  onChange={(e) => setNewPms({ ...newPms, nom: e.target.value })}
                />
              </div>
              <div>
                <Label>Version</Label>
                <Input
                  value={newPms.version}
                  onChange={(e) => setNewPms({ ...newPms, version: e.target.value })}
                  placeholder="Ex: 1.0"
                />
              </div>
              <div>
                <Label>Fichier *</Label>
                <Input type="file" accept=".pdf,.doc,.docx" onChange={handleFileUpload} />
                {uploading && <p className="text-sm text-secondary mt-1">Upload en cours...</p>}
                {newPms.fichier_url && (
                  <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> Fichier prêt
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Annuler</Button>
              <Button onClick={handleSavePms} disabled={!newPms.fichier_url || uploading}>
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ==================== EXPORT SECTION ====================
function ExportSection({ foodTruckId }) {
  const [dateDebut, setDateDebut] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
  );
  const [dateFin, setDateFin] = useState(new Date().toISOString().split("T")[0]);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/hygiene/export/pdf?date_debut=${dateDebut}&date_fin=${dateFin}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error("Erreur lors de l'export");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport_hygiene_${dateDebut}_${dateFin}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export PDF généré");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="w-5 h-5 text-primary" />
          Export PDF pour Contrôle DDPP
        </CardTitle>
        <CardDescription>Générez un récapitulatif pour les inspections</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date début</Label>
              <Input
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
              />
            </div>
            <div>
              <Label>Date fin</Label>
              <Input
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={handleExport} disabled={exporting} className="w-full">
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Génération en cours...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Télécharger le rapport PDF
              </>
            )}
          </Button>
          <p className="text-xs text-secondary text-center">
            Le rapport inclut : réceptions, températures, cuissons, nettoyages et productions
          </p>
        </div>
      </CardContent>
    </Card>
  );
}


// ==================== STATISTIQUES TAB ====================
function StatistiquesTab({ foodTruckId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30");

  const COLORS = ["#22c55e", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

  const loadStats = useCallback(async () => {
    if (!foodTruckId) return;
    
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/hygiene/statistics/${foodTruckId}?period=${period}`);
      setStats(data);
    } catch (error) {
      console.error("Erreur chargement statistiques:", error);
      toast.error("Erreur lors du chargement des statistiques");
    } finally {
      setLoading(false);
    }
  }, [foodTruckId, period]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (!foodTruckId) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Sélectionnez un food truck pour voir les statistiques</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
          <p className="mt-4 text-muted-foreground">Chargement des statistiques...</p>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Aucune donnée disponible</p>
        </CardContent>
      </Card>
    );
  }

  const { summary, temperature_trends, equipment_stats, cleaning_trends, zone_stats, reception_trends, fournisseur_stats, recent_issues } = stats;

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Statistiques Hygiène
        </h3>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Période" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 derniers jours</SelectItem>
            <SelectItem value="30">30 derniers jours</SelectItem>
            <SelectItem value="90">3 derniers mois</SelectItem>
            <SelectItem value="365">12 derniers mois</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`${summary.compliance_rate >= 95 ? 'border-green-500' : summary.compliance_rate >= 80 ? 'border-yellow-500' : 'border-red-500'} border-2`}>
          <CardContent className="p-4 text-center">
            <div className={`text-3xl font-bold ${summary.compliance_rate >= 95 ? 'text-green-600' : summary.compliance_rate >= 80 ? 'text-yellow-600' : 'text-red-600'}`}>
              {summary.compliance_rate}%
            </div>
            <p className="text-sm text-muted-foreground">Taux de conformité</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">{summary.total_temperatures}</div>
            <p className="text-sm text-muted-foreground">Relevés températures</p>
            <p className="text-xs text-green-600">{summary.temperatures_conformes} conformes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-amber-600">{summary.total_receptions}</div>
            <p className="text-sm text-muted-foreground">Réceptions</p>
            <p className="text-xs text-green-600">{summary.receptions_conformes} conformes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-purple-600">{summary.total_cleanings}</div>
            <p className="text-sm text-muted-foreground">Nettoyages</p>
          </CardContent>
        </Card>
      </div>

      {/* Temperature Trends Chart */}
      {temperature_trends && temperature_trends.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Thermometer className="w-5 h-5 text-blue-500" />
              Évolution des températures
            </CardTitle>
            <CardDescription>Moyenne quotidienne des relevés</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={temperature_trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getDate()}/${date.getMonth() + 1}`;
                    }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    label={{ value: '°C', angle: -90, position: 'insideLeft', fontSize: 12 }}
                  />
                  <Tooltip 
                    labelFormatter={(value) => new Date(value).toLocaleDateString('fr-FR')}
                    formatter={(value, name) => {
                      const labels = { moyenne: 'Moyenne', min: 'Min', max: 'Max' };
                      return [`${value}°C`, labels[name] || name];
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="moyenne" stroke="#3b82f6" strokeWidth={2} name="Moyenne" dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="min" stroke="#22c55e" strokeWidth={1} strokeDasharray="5 5" name="Min" dot={false} />
                  <Line type="monotone" dataKey="max" stroke="#ef4444" strokeWidth={1} strokeDasharray="5 5" name="Max" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Equipment Stats */}
        {equipment_stats && equipment_stats.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ThermometerSnowflake className="w-5 h-5 text-cyan-500" />
                Températures par équipement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={equipment_stats} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                    <XAxis type="number" tick={{ fontSize: 12 }} unit="°C" />
                    <YAxis type="category" dataKey="equipement" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip formatter={(value) => `${value}°C`} />
                    <Bar dataKey="moyenne" fill="#3b82f6" name="Moyenne" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 space-y-2">
                {equipment_stats.map((eq, i) => (
                  <div key={i} className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">{eq.equipement}</span>
                    <div className="flex items-center gap-2">
                      <span>{eq.moyenne}°C</span>
                      {eq.non_conformes > 0 && (
                        <Badge variant="destructive" className="text-xs">{eq.non_conformes} NC</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cleaning by Zone */}
        {zone_stats && zone_stats.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SprayCan className="w-5 h-5 text-green-500" />
                Nettoyages par zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={zone_stats}
                      dataKey="count"
                      nameKey="zone"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label={({ zone, percent }) => `${zone} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={{ stroke: '#666', strokeWidth: 1 }}
                    >
                      {zone_stats.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Cleaning Activity Trend */}
      {cleaning_trends && cleaning_trends.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-500" />
              Activité nettoyage
            </CardTitle>
            <CardDescription>Nombre de nettoyages par jour</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cleaning_trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getDate()}/${date.getMonth() + 1}`;
                    }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    labelFormatter={(value) => new Date(value).toLocaleDateString('fr-FR')}
                    formatter={(value) => [value, 'Nettoyages']}
                  />
                  <Bar dataKey="nb_nettoyages" fill="#22c55e" radius={[4, 4, 0, 0]} name="Nettoyages" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Issues */}
      {recent_issues && recent_issues.length > 0 && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Dernières non-conformités
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recent_issues.map((issue, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                  <Badge variant="outline" className="text-xs shrink-0">
                    {issue.type === 'temperature' ? 'Temp.' : 
                     issue.type === 'reception' ? 'Récep.' : 'Cuisson'}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{issue.description}</p>
                    <p className="text-xs text-muted-foreground">{new Date(issue.date).toLocaleDateString('fr-FR')}</p>
                    {issue.action && (
                      <p className="text-xs text-red-600 mt-1">Action : {issue.action}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fournisseur Stats */}
      {fournisseur_stats && fournisseur_stats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-amber-500" />
              Réceptions par fournisseur
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fournisseur</TableHead>
                  <TableHead className="text-center">Total</TableHead>
                  <TableHead className="text-center">Non conformes</TableHead>
                  <TableHead className="text-center">Taux</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fournisseur_stats.map((f, i) => {
                  const rate = f.total > 0 ? Math.round((f.total - f.non_conformes) / f.total * 100) : 100;
                  return (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{f.fournisseur}</TableCell>
                      <TableCell className="text-center">{f.total}</TableCell>
                      <TableCell className="text-center">
                        {f.non_conformes > 0 ? (
                          <Badge variant="destructive">{f.non_conformes}</Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={rate >= 95 ? "default" : rate >= 80 ? "secondary" : "destructive"} className={rate >= 95 ? "bg-green-500" : ""}>
                          {rate}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
